import React, { FC, useEffect } from 'react';
import { SidebarHeading } from '../../SidebarHeading';
import { AddLink, FormatQuote } from '@mui/icons-material';
import { Alert, AlertTitle, Button, FormControlLabel, Stack, Switch } from '@mui/material';
import { useReferencesContext } from '../ReferencesContext';
import { useGuideCmsContext } from '../../../GuideCmsContext';
import { StyledHeader } from '../styles';
import { ReferenceCandidateTypes } from '../../../tiptap/extensions/plugins/ReferenceCandidateHighlight';
import { StyledLink } from '../../../../../Components';
import { findTextOccurrences } from '../../../tiptap/tiptapHelper';
import { useNumberedNodes } from '../../../tiptap/numbering';
import { useQuery } from '@apollo/client';
import { GET_GUIDE_AND_LINKS } from '../../../../guide.graphql';
import { autoMapInternalReferences, autoMapGuides, GuideInfo } from '../autoMapping';

export const Candidates: FC = () => {
  const { isReferenceCandidateHighlightEnabled, setIsReferenceCandidateHighlightEnabled, editor, guideId, openMigrateCitationsDialog } =
    useGuideCmsContext();

  const { candidates, hasCandidates, ieeeCitationLinks, hasIeeeCitationLinks } = useReferencesContext();
  const numberedNodes = useNumberedNodes();
  const { data: guidesData } = useQuery(GET_GUIDE_AND_LINKS, { variables: { id: guideId } });

  // Sync the highlight state with the editor whenever it changes
  useEffect(() => {
    if (editor && !editor.isDestroyed) {
      editor.commands.setReferenceCandidateHighlight(isReferenceCandidateHighlightEnabled);
    }
  }, [editor, isReferenceCandidateHighlightEnabled]);

  const handleToggleHighlight = (enabled: boolean) => {
    setIsReferenceCandidateHighlightEnabled(enabled);
  };

  const handleAutoMapInternal = (category: 'table' | 'figure' | 'heading') => {
    if (!editor) return;
    const candidateList = category === 'table' ? candidates.tables : category === 'figure' ? candidates.figures : candidates.headings;
    autoMapInternalReferences(editor, numberedNodes, candidateList, category);
  };

  const handleAutoMapGuides = () => {
    if (!editor || !guidesData?.guides) return;
    const eligibleGuides: GuideInfo[] = guidesData.guides
      .filter((g): g is NonNullable<typeof g> => !!g && g.id !== guideId && !!g.docName)
      .map((g) => ({ id: g.id, docId: g.docId, docName: g.docName! }));
    autoMapGuides(editor, candidates.guides, eligibleGuides);
  };

  const ieeeBanner = hasIeeeCitationLinks ? (
    <Alert
      severity="info"
      icon={<FormatQuote />}
      sx={{ mb: 1 }}
      action={
        <Button size="small" onClick={() => openMigrateCitationsDialog()}>
          Migrér
        </Button>
      }>
      <AlertTitle>Migrér litteraturhenvisninger</AlertTitle>
      Dokumentet inneholder {ieeeCitationLinks.length} litteraturhenvisning{ieeeCitationLinks.length === 1 ? '' : 'er'} på gammelt format som kan
      migreres til litteraturhenvisninger.
    </Alert>
  ) : null;

  if (!hasCandidates) {
    return (
      <Stack>
        {ieeeBanner}
        <Alert severity="success">Dokumentet inneholder ingen potensielle referanser.</Alert>
      </Stack>
    );
  }

  return (
    <div>
      <SidebarHeading icon={<AddLink />}>Potensielle referanser</SidebarHeading>
      {ieeeBanner}

      <Alert severity="warning">Dokumentet innholder potensielle referanser som ikke er koblet opp.</Alert>
      <FormControlLabel
        control={<Switch checked={isReferenceCandidateHighlightEnabled} onChange={(e) => handleToggleHighlight(e.target.checked)} />}
        label="Markér potensielle referanser i teksten"
      />
      {candidates.tables.length > 0 && (
        <>
          <CategoryHeader title="Potensielle tabeller" onAutoMap={() => handleAutoMapInternal('table')} />
          {candidates.tables.map((name) => (
            <Candidate key={name} text={name} type="table" />
          ))}
        </>
      )}
      {candidates.figures.length > 0 && (
        <>
          <CategoryHeader title="Potensielle figurer" onAutoMap={() => handleAutoMapInternal('figure')} />
          {candidates.figures.map((name) => (
            <Candidate key={name} text={name} type="figure" />
          ))}
        </>
      )}
      {candidates.headings.length > 0 && (
        <>
          <CategoryHeader title="Potensielle punkter" onAutoMap={() => handleAutoMapInternal('heading')} />
          {candidates.headings.map((name) => (
            <Candidate key={name} text={name} type="heading" />
          ))}
        </>
      )}
      {candidates.guides.length > 0 && (
        <>
          <CategoryHeader title="Potensielle anvisninger" onAutoMap={handleAutoMapGuides} />
          {candidates.guides.map((name) => (
            <Candidate key={name} text={name} type="guide" />
          ))}
        </>
      )}
      {candidates.standards.length > 0 && (
        <>
          <StyledHeader>Potensielle Standarder</StyledHeader>
          {candidates.standards.map((name) => (
            <Candidate key={name} text={name} type="standard" />
          ))}
        </>
      )}
    </div>
  );
};

const CategoryHeader: FC<{ title: string; onAutoMap: () => void }> = ({ title, onAutoMap }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <StyledHeader>{title}</StyledHeader>
    <Button size="small" onClick={onAutoMap} startIcon={<AddLink />}>
      Koble opp alle
    </Button>
  </div>
);

const Candidate: FC<{ text: string; type: ReferenceCandidateTypes }> = ({ text, type }) => {
  const { editor, openInternalLinksDialog, openGuideLinkDialog, openStandardLinkDialog } = useGuideCmsContext();

  const handleOnReferenceCandidateClick = (text: string) => {
    if (!editor) return;
    const first = findTextOccurrences(editor.state.doc, text)[0];

    if (!first) return;
    editor.chain().focus().setTextSelection({ from: first.from, to: first.to }).scrollIntoViewWithOffset().run();

    if (type === 'guide') {
      openGuideLinkDialog(text);
    } else if (type === 'standard') {
      openStandardLinkDialog(text);
    } else {
      openInternalLinksDialog(text);
    }
  };

  return (
    <div>
      <StyledLink
        to=""
        onClick={(e) => {
          e.preventDefault();
          handleOnReferenceCandidateClick(text);
        }}>
        {text}
      </StyledLink>
    </div>
  );
};
